import { CheckCircle2, GitPullRequestArrow, XCircle } from "lucide-react";
import { Link } from "react-router";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmActionDecisions } from "@/hooks/use-crm-action-decisions";
import { formatMonthDay } from "@/lib/i18n";
import type { CrmActionDecisionNamedBucket } from "@/hooks/use-crm-action-decisions";

const DECISION_CLASS: Record<string, string> = {
  accepted: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  dismissed: "border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
};

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "Unknown action";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function bucketLabel(bucket: CrmActionDecisionNamedBucket | undefined): string {
  return prettyActionLabel(
    bucket?.actionRef
    ?? bucket?.intent
    ?? bucket?.sourceEventType
    ?? bucket?.signalFamily
    ?? bucket?.sourceSurface
    ?? bucket?.executionPlanStatus
    ?? bucket?.readinessStatus,
  );
}

function formatRate(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function shortId(value: string | null | undefined): string | null {
  return value ? value.slice(0, 8) : null;
}

function targetLinkForDecision(decision: {
  contactId: string | null;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
}): { to: string; label: string } | null {
  if (decision.contactId) {
    return { to: `/crm/contacts/${decision.contactId}`, label: "Contact" };
  }
  if (decision.targetEntityType === "deal" && decision.targetEntityId) {
    return { to: `/crm/deals/${decision.targetEntityId}`, label: "Deal" };
  }
  if (decision.targetEntityType === "company" && decision.targetEntityId) {
    return { to: `/crm/companies/${decision.targetEntityId}`, label: "Company" };
  }
  if (decision.targetEntityType === "task") {
    return { to: "/crm/tasks", label: "Task" };
  }
  if (decision.targetEntityType === "activity") {
    return { to: "/crm/activities", label: "Activity" };
  }
  return null;
}

interface ActionDecisionSummaryProps {
  contractRef?: string;
  signalFamily?: string;
  sourceEventType?: string;
}

export function ActionDecisionSummary({
  contractRef,
  signalFamily,
  sourceEventType,
}: ActionDecisionSummaryProps = {}) {
  const decisionFilters = {
    ...(contractRef ? { actionRef: contractRef } : {}),
    ...(signalFamily ? { signalFamily } : {}),
    ...(sourceEventType ? { sourceEventType } : {}),
  };
  const { data, isLoading, error, refetch } = useCrmActionDecisions(decisionFilters);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-44" />
        </div>
        <SkeletonCard className="h-24" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
        Could not load action decision stream.
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  const totals = data?.totals ?? { accepted: 0, dismissed: 0, total: 0 };
  const acceptanceRate = totals.total > 0 ? Math.round((totals.accepted / totals.total) * 100) : 0;
  const topAction = data?.byActionRef[0];
  const topExecution = data?.byExecutionPlan[0];
  const topSourceSurface = data?.bySourceSurface[0];
  const topSourceEventType = data?.bySourceEventType?.[0];
  const topSignalFamily = data?.bySignalFamily?.[0];
  const signalFamilies = data?.bySignalFamily?.slice(0, 4) ?? [];
  const recent = data?.recent.slice(0, 5) ?? [];

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <GitPullRequestArrow className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Action Decision Stream</h2>
            <span className="rounded-full border border-[var(--color-primary-border)] bg-[var(--color-primary-light)] px-1.5 py-0.5 text-[10px] text-[var(--color-primary)]">
              Shadow
            </span>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Accepted and dismissed recommendations. These are review signals, not executed automations.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {data?.windowDays ?? 30}d
        </span>
      </div>

      {contractRef && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Decisions scoped to {prettyActionLabel(contractRef)}
        </p>
      )}
      {(signalFamily || sourceEventType) && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Signal scope {[
            signalFamily ? `${prettyActionLabel(signalFamily)} family` : null,
            sourceEventType ? prettyActionLabel(sourceEventType) : null,
          ].filter(Boolean).join(" · ")}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Reviewed</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{totals.total}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-success)]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Accepted
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-success)]">{totals.accepted}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
            <XCircle className="h-3.5 w-3.5" />
            Dismissed
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{totals.dismissed}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Acceptance</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{acceptanceRate}%</p>
        </div>
      </div>

      {(topAction || topExecution || topSourceSurface || topSourceEventType || topSignalFamily) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {topAction && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Most reviewed action</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topAction)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topAction.accepted} accepted, {topAction.dismissed} dismissed
              </p>
            </div>
          )}
          {topExecution && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Execution posture</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topExecution)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topExecution.total} reviewed recommendations
              </p>
            </div>
          )}
          {topSourceEventType && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Most reviewed source event</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topSourceEventType)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSourceEventType.accepted} accepted, {topSourceEventType.dismissed} dismissed
              </p>
            </div>
          )}
          {topSignalFamily && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Most reviewed signal family</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topSignalFamily)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSignalFamily.accepted} accepted, {topSignalFamily.dismissed} dismissed
              </p>
            </div>
          )}
          {topSourceSurface && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Most useful surface</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topSourceSurface)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSourceSurface.accepted} accepted, {topSourceSurface.dismissed} dismissed
              </p>
            </div>
          )}
        </div>
      )}

      {signalFamilies.length > 0 && (
        <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)]">Decision mix by signal family</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {signalFamilies.map((bucket) => {
              const acceptedRate = bucket.total > 0 ? bucket.accepted / bucket.total : 0;

              return (
                <div
                  key={bucket.signalFamily ?? "unknown"}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-[var(--color-foreground)]">{bucketLabel(bucket)}</p>
                    <span className="text-[var(--color-primary)]">{formatRate(acceptedRate)}</span>
                  </div>
                  <p className="mt-1 text-[var(--color-muted-foreground)]">
                    {bucket.accepted} accepted · {bucket.dismissed} dismissed · {bucket.total} reviewed
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)]">Recent decisions</p>
          {recent.map((decision) => {
            const targetLink = targetLinkForDecision(decision);

            return (
              <div
                key={decision.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs"
              >
              <div className="min-w-0">
                <p className="font-medium text-[var(--color-foreground)]">
                  {prettyActionLabel(decision.actionRef)}
                </p>
                <p className="text-[var(--color-muted-foreground)]">
                  {prettyActionLabel(decision.intent)} from {prettyActionLabel(decision.sourceSurface)} - {formatMonthDay(decision.decidedAt)}
                </p>
                <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
                  {prettyActionLabel(decision.readinessStatus)} · {prettyActionLabel(decision.executionPlanStatus)}
                  {decision.sourceEventType ? ` · ${prettyActionLabel(decision.sourceEventType)}` : ""}
                  {decision.signalFamily ? ` · ${prettyActionLabel(decision.signalFamily)} family` : ""}
                  {decision.sourceEventId ? ` · source ${shortId(decision.sourceEventId)}` : ""}
                  {decision.materialization
                    ? decision.materialization.manualResolution
                      ? " · manual resolution recorded"
                      : ` · materialized ${prettyActionLabel(decision.materialization.materializedEntityType)} ${shortId(decision.materialization.materializedEntityId) ?? ""}`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 ${DECISION_CLASS[decision.decision]}`}>
                  {decision.decision}
                </span>
                {decision.materialization && (
                  <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-0.5 text-[var(--color-success)]">
                    {decision.materialization.manualResolution
                      ? "manual resolved"
                      : decision.materialization.materializationStatus ?? "materialized"} - {formatMonthDay(decision.materialization.materializedAt)}
                  </span>
                )}
                {decision.observedOutcome && decision.observedOutcome.confidence !== "none" && (
                  <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[var(--color-primary)]">
                    {formatRate(decision.observedOutcome.materializationRate)} materialized - {decision.observedOutcome.confidence}
                    {decision.observedOutcome.evidenceAgeDays == null ? "" : ` - ${decision.observedOutcome.evidenceAgeDays}d old`}
                  </span>
                )}
                {decision.outcomeScoring && decision.outcomeScoring.adjustedConfidence !== decision.outcomeScoring.baseConfidence && (
                  <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-0.5 text-[var(--color-success)]">
                    {formatRate(decision.outcomeScoring.adjustedConfidence)} outcome-adjusted
                  </span>
                )}
                {targetLink && (
                  <Link
                    to={targetLink.to}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    {targetLink.label}
                  </Link>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
